import type { MaybeRefOrGetter } from 'vue'

// Widths of the text column: full width below lg, next to the 300px sidebar on lg,
// three of four grid columns from xl (same as the news cover in news-page/details/Header.vue).
// vw values need a screen key: getSizes resolves them against that screen's width.
const CONTENT_IMAGE_SIZES = 'xs:100vw sm:100vw md:100vw lg:700px xl:975px'

const IMG_TAG = /<img\b[^>]*>/gi
const SRC_ATTR = /\ssrc\s*=\s*(["'])(.*?)\1/i

const escapeAttr = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

const hasAttr = (tag: string, name: string) => new RegExp(`\\s${name}\\s*=`, 'i').test(tag)

/**
 * Rewrites editor images hosted on our domains (image.domains) to the IPX endpoint, so they
 * are served as resized AVIF like the rest of the site. The HTML string is rewritten before
 * rendering: the SSR markup already holds the IPX URLs and the browser never loads the
 * original JPEG/PNG. External images and data: URIs are left untouched.
 */
export function useContentImages(html: MaybeRefOrGetter<string | null | undefined>) {
  const img = useImage()
  const domains: string[] = img.options.domains ?? []

  const isOwnImage = (src: string) => {
    if (!src.startsWith('http')) return false

    try {
      return domains.includes(new URL(src).host)
    } catch {
      return false
    }
  }

  const rewriteTag = (tag: string) => {
    const match = tag.match(SRC_ATTR)
    const src = match?.[2]

    if (!match || !src || !isOwnImage(src) || hasAttr(tag, 'srcset')) return tag

    const sizes = img.getSizes(src, { sizes: CONTENT_IMAGE_SIZES })
    const attrs = [
      ` src="${escapeAttr(sizes.src ?? src)}"`,
      sizes.srcset && ` srcset="${escapeAttr(sizes.srcset)}"`,
      sizes.sizes && ` sizes="${escapeAttr(sizes.sizes)}"`,
      !hasAttr(tag, 'loading') && ' loading="lazy"',
      !hasAttr(tag, 'decoding') && ' decoding="async"'
    ]

    return tag.replace(match[0], attrs.filter(Boolean).join(''))
  }

  return computed(() => {
    const value = toValue(html)

    return value ? value.replace(IMG_TAG, rewriteTag) : value
  })
}
